import React, { FC, useCallback, useEffect, useState } from 'react';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import {
    Grid,
    Select,
    InputLabel,
    MenuItem,
    Button,
    Box,
    Fade,
    LinearProgress,
    Tooltip,
    IconButton,
    Menu,
    Switch,
} from '@mui/material';
import Swal from 'sweetalert2';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RoomIcon from '@mui/icons-material/Room';
import FormControl from '@mui/material/FormControl';
import { useTranslation } from 'react-i18next';

import { SCUIContent } from '../../atomic/organisms';
import { SCUIAutoCompleteInput, SCUIButton } from '../../atomic/atoms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../atomic/molecules';
import { OmnichannelQuery } from '../../services/query';
import { OmnichannelCommand } from '../../services/command';
import { StorePickUpEditDrawer } from './components/StorePickUpEditDrawer';
import { mapStorePickupApiItem, SPU, StorePickUpRow } from './storePickUp.classes';

export const StorePickUp: FC<any> = () => {
    const [statusName, setStatusName] = useState<any>(-1);
    const [firstCall, setFirstCall] = useState(true);
    const [displayedStoresAutocomlpete, setDisplayedStoresAutocomlpete] = React.useState<StorePickUpRow[]>([]);
    const [storeName, setStoreName] = React.useState<string[]>([]);
    const [storePickupList, setStorePickupList] = useState<StorePickUpRow[]>();
    const [loading, setLoading] = React.useState(true);
    const [excelProps, setExcelProps] = useState('');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editRow, setEditRow] = useState<StorePickUpRow | null>(null);

    const { t } = useTranslation();

    const parseListResponse = (res: any): StorePickUpRow[] => {
        const raw = res?.data ?? res;
        const items = Array.isArray(raw) ? raw : raw?.data;
        if (!Array.isArray(items)) {
            return [];
        }
        return items.map(mapStorePickupApiItem);
    };

    const applyClientFilters = (rows: StorePickUpRow[]): StorePickUpRow[] => {
        let filtered = rows;
        if (storeName.length > 0) {
            filtered = filtered.filter((row) => storeName.includes(row.storeCode));
        }
        if (statusName === 1) {
            filtered = filtered.filter((row) => row.visible);
        } else if (statusName === 0) {
            filtered = filtered.filter((row) => !row.visible);
        }
        return filtered;
    };

    const loadStorePickupList = useCallback((): Promise<void> => {
        setLoading(true);
        const hasServerFilter = storeName.length > 0 || statusName !== -1;

        const request = hasServerFilter
            ? OmnichannelQuery.FilterStorePickup({
                  storeName,
                  status: statusName,
              })
            : OmnichannelQuery.StorePickupList();

        return request
            .then((res: any) => {
                let parseData = parseListResponse(res);
                if (!hasServerFilter) {
                    parseData = applyClientFilters(parseData);
                }
                setStorePickupList(parseData);
                if (firstCall) {
                    const autocompleteSource = hasServerFilter
                        ? parseData
                        : parseListResponse(res);
                    setDisplayedStoresAutocomlpete(autocompleteSource);
                    setFirstCall(false);
                }
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [firstCall, statusName, storeName]);

    const updateStoreVisibilityInList = (storeId: StorePickUpRow['id'], newVisibility: boolean) => {
        setStorePickupList((prev) =>
            (prev || []).map((element) =>
                element.id === storeId ? { ...element, visible: newVisibility } : element
            )
        );
        setLoading(false);
        Swal.fire(t('general.updated'), ' ', 'success');
    };

    const handleStorePickupError = (error: unknown) => {
        console.log('Hata :>> ', error);
        setLoading(false);
    };

    const performStorePickupUpdate = (store: StorePickUpRow, newVisibility: boolean) => {
        setLoading(true);
        OmnichannelCommand.PostUpdateStorePickup({
            storeId: store.id,
            isVisible: newVisibility,
        })
            .then(() => {
                updateStoreVisibilityInList(store.id, newVisibility);
            })
            .catch(handleStorePickupError);
    };

    const handleUserActivityChange = (event: React.ChangeEvent<HTMLInputElement>, store: StorePickUpRow) => {
        const newVisibility = event.target.checked;
        Swal.fire({
            text: t('authorizationManagement.listUser.surePassiveText') + ' ' + store.name,
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.update'),
        }).then((result) => {
            if (result.isConfirmed) {
                performStorePickupUpdate(store, newVisibility);
            }
        });
    };

    const handleEditRow = (row: StorePickUpRow) => {
        setEditRow(row);
        setIsDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setEditRow(null);
    };

    const getColumnMapping = () => ({
        storeCode: t('general.storeCode'),
        name: t('general.storeName'),
        visible: t('general.status'),
        capacity: t('storePickUp.capacity'),
        remainingCapacity: t('storePickUp.remainingCapacity'),
        address: t('general.address'),
    });

    const mapRowForExcel = (row: StorePickUpRow, columnMapping: Record<string, string>) => {
        const mappedRow: Record<string, string | number | boolean> = {};
        Object.keys(row).forEach((key) => {
            if (columnMapping[key]) {
                if (key === 'visible') {
                    mappedRow[columnMapping[key]] = row.visible ? t('general.active') : t('general.passive');
                } else {
                    mappedRow[columnMapping[key]] = (row as any)[key];
                }
            }
        });
        return mappedRow;
    };

    const BuildExcelData = () => {
        if (!storePickupList) {
            return;
        }
        const columnMapping = getColumnMapping();
        const parseData = storePickupList.map((row) => mapRowForExcel(row, columnMapping));
        setExcelProps(parseData as any);
    };

    const columns: ExtendedGridColDef[] = [
        { field: 'id', headerName: 'ID', width: 90, hide: true },
        {
            field: 'storeCode',
            headerName: t('general.storeCode'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => (
                <span className={SPU.cellStoreCode} data-store-code={params.row.storeCode}>
                    {params.row.storeCode}
                </span>
            ),
        },
        {
            field: 'name',
            headerName: t('general.storeName'),
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params: any) => (
                <span className={SPU.cellStoreName} data-store-name={params.row.name}>
                    {params.row.name}
                </span>
            ),
        },
        {
            field: 'visible',
            headerName: t('general.status'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={SPU.cellStatus}>
                    <Switch
                        checked={params.row.visible}
                        onChange={(e) => handleUserActivityChange(e, params.row)}
                        className={SPU.inputStatusSwitch}
                        data-store-id={params.row.id}
                        data-status-visible={params.row.visible}
                    />
                </div>
            ),
        },
        {
            field: 'capacity',
            headerName: t('storePickUp.capacity'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <span className={SPU.cellCapacity}>{params.row.capacity ?? '-'}</span>
            ),
        },
        {
            field: 'remainingCapacity',
            headerName: t('storePickUp.remainingCapacity'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <span className={SPU.cellRemainingCapacity}>
                    {params.row.remainingCapacity ?? '-'}
                </span>
            ),
        },
        {
            field: 'address',
            headerName: t('general.address'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <div className={SPU.cellAddress}>
                        <Tooltip title={params.row.address} placement="top-start">
                            <p className={SPU.cellAddressText} data-store-id={params.row.id}>
                                {params.row.address}
                            </p>
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            field: 'detay',
            headerName: t('general.location'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const onclick = () => {
                    window.open(
                        'https://maps.google.com/maps?q=loc:' + params.row.lattitude + ',' + params.row.longitude,
                        '_blank',
                        'noopener,noreferrer'
                    );
                };
                return (
                    <div className={SPU.cellLocation}>
                        <Button onClick={onclick} className={SPU.btnOpenLocation} data-store-id={params.row.id}>
                            <RoomIcon className="svg_icons" />
                        </Button>
                    </div>
                );
            },
        },
        {
            field: 'actions',
            headerName: t('general.detail'),
            flex: 0.6,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: (cellData: any) => (
                <div className={SPU.cellActions}>
                    <PopupState variant="popover" popupId={`store-pickup-actions-${cellData.row.id}`}>
                        {(popupState: any) => (
                            <>
                                <IconButton {...bindTrigger(popupState)} className={SPU.btnActionsMenu}>
                                    <MoreVertIcon className="svg_icons" />
                                </IconButton>
                                <Menu {...bindMenu(popupState)}>
                                    <MenuItem
                                        onClick={() => {
                                            popupState.close();
                                            handleEditRow(cellData.row);
                                        }}
                                    >
                                        {t('general.edit')}
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </PopupState>
                </div>
            ),
        },
    ];

    useEffect(() => {
        if (storePickupList) {
            BuildExcelData();
        }
    }, [storePickupList]);

    useEffect(() => {
        loadStorePickupList();
    }, []);

    return (
        <SCUIContent className={SPU.page}>
            <Box className={SPU.loading}>
                <Fade in={loading} unmountOnExit>
                    <LinearProgress className={SPU.loadingProgress} />
                </Fade>
            </Box>
            <Grid direction="row" sx={{ width: '100%' }} className={SPU.root}>
                <Box className={SPU.breadcrumb}>
                    <SCUIBreadCrumb title={t('dashboard.storePickup')} componentLink={'StorePickUp'} />
                </Box>
                <Grid sx={{ marginTop: '8px', width: '60%' }} container spacing={2} className={SPU.filters}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 5,
                            lg: 5,
                            xl: 5,
                        }}
                        className={`${SPU.filterStore} ${SPU.inputStore}`}
                    >
                        {displayedStoresAutocomlpete && (
                            <SCUIAutoCompleteInput
                                list={displayedStoresAutocomlpete}
                                onChange={(_: any, newValue: any) => {
                                    setStoreName(newValue.map((item: any) => item.storeCode));
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        padding: '0 0 0 8px',
                                    },
                                    '& .MuiInputBase-root.Mui-focused': {
                                        padding: '8px',
                                    },
                                }}
                                limitTags={1}
                                txtPlaceHolder={t('general.storeName')}
                                searchValue="storeCodeAndName"
                                multiple={true}
                            />
                        )}
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 5,
                            lg: 5,
                            xl: 5,
                        }}
                        className={SPU.filterStatus}
                    >
                        <FormControl fullWidth>
                            <InputLabel id="statusSelectLabel">{t('general.operation')}</InputLabel>
                            <Select
                                labelId="statusSelectLabel"
                                fullWidth
                                id="statusSelect"
                                value={statusName}
                                label={t('general.status')}
                                className={SPU.inputStatus}
                                onChange={(event: any) => {
                                    setStatusName(event.target.value);
                                }}
                            >
                                <MenuItem value={-1} className={SPU.statusMenuItem} data-status-value={-1}>
                                    {t('general.all')}
                                </MenuItem>
                                <MenuItem value={1} className={SPU.statusMenuItem} data-status-value={1}>
                                    {t('general.active')}
                                </MenuItem>
                                <MenuItem value={0} className={SPU.statusMenuItem} data-status-value={0}>
                                    {t('general.passive')}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 2,
                            lg: 2,
                            xl: 2,
                        }}
                        className={SPU.filterAction}
                    >
                        <SCUIButton
                            text={t('general.search')}
                            type="primary"
                            buttonType="submit"
                            sizeType="fixed"
                            size={'s'}
                            onClick={loadStorePickupList}
                            className={SPU.btnSearch}
                        />
                    </Grid>
                </Grid>
            </Grid>
            {storePickupList && (
                <Box className={SPU.grid}>
                    <SCUICustomDataGrid
                        rows={storePickupList}
                        columns={columns}
                        excelDownloadButton={true}
                        excelDownloadData={excelProps}
                        excelDownloadFolderName={'MağazadanTeslimat'}
                        initialState={{
                            sorting: {
                                sortModel: [
                                    {
                                        field: 'id',
                                        sort: 'asc',
                                    },
                                ],
                            },
                        }}
                    />
                </Box>
            )}
            <StorePickUpEditDrawer
                isOpen={isDrawerOpen}
                editRow={editRow}
                onClose={handleDrawerClose}
                onRefreshList={loadStorePickupList}
            />
        </SCUIContent>
    );
};
